import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  CubeIcon,
  GlobeAltIcon,
  PowerIcon,
  PresentationChartBarIcon,
} from "@heroicons/react/24/outline";
import { SocketProvider } from "@via/context/socket-context";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import {
  useLaunchApplication,
  useTerminateAppInstance,
} from "@via/pages/apps/apps.api.tsx";
import { AppReport } from "@via/pages/projects/runs/reports/report-types/app-report.tsx";
import { DisplayableReport } from "@via/pages/projects/runs/reports/report-types/displayable-report.tsx";
import { DownloadableReport } from "@via/pages/projects/runs/reports/report-types/downloadable-report.tsx";
import { TabularReport } from "@via/pages/projects/runs/reports/report-types/tabular-report.tsx";
import {
  MarkdownReport,
  TextReport,
} from "@via/pages/projects/runs/reports/report-types/text-report.tsx";
import { useReportTable } from "@via/pages/projects/runs/reports/hooks/use-report-table.ts";
import {
  MAXIMUM_REPORT_FILE_SIZE,
  MAXIMUM_TABULAR_FILE_SIZE,
  REPORT_TYPES,
} from "@via/pages/projects/runs/reports/utils/constants.ts";
import {
  downloadTextFile,
  getDirectoryName,
  getReportUrl,
  processPercentData,
  toTsv,
} from "@via/pages/projects/runs/reports/utils/helpers.ts";
import { useRunAppStatus } from "@via/pages/projects/runs/runs.api";
import { classNames } from "@via/utils/helpers.tsx";
import { AppStatus, RunReportResource } from "common";
import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { ROUTES, RunTabs } from "@via/utils/constants";
import ContextButton from "@via/components/context-button/context-button";
import { TableContextMenuInterface } from "@via/utils/interfaces";

const REPORT_COMPONENTS = {
  DISPLAYABLE: DisplayableReport,
  TABULAR: TabularReport,
  DOWNLOADABLE: DownloadableReport,
  RUNNABLE: AppReport,
  MARKDOWN: MarkdownReport,
  TEXT: TextReport,
};

export const getReportType = (
  extension: string,
  fileSize: number,
  reportTypes: string[] = []
): keyof typeof REPORT_COMPONENTS => {
  const ext = (extension ?? "").toLowerCase();

  // Text and markdown only ever fetch a bounded prefix, so they are checked
  // before the size cap that sends everything else to download.
  if (REPORT_TYPES.MARKDOWN.includes(ext)) {
    return "MARKDOWN";
  }
  if (
    REPORT_TYPES.TEXT.includes(ext) ||
    (reportTypes.includes("text") &&
      !REPORT_TYPES.DISPLAYABLE.includes(ext) &&
      !REPORT_TYPES.TABULAR.includes(ext) &&
      !REPORT_TYPES.RUNNABLE.includes(ext))
  ) {
    return "TEXT";
  }

  if (fileSize >= MAXIMUM_REPORT_FILE_SIZE) {
    return "DOWNLOADABLE";
  }

  if (REPORT_TYPES.DISPLAYABLE.includes(ext)) {
    return "DISPLAYABLE";
  } else if (REPORT_TYPES.TABULAR.includes(ext)) {
    return "TABULAR";
  } else if (REPORT_TYPES.RUNNABLE.includes(ext)) {
    return "RUNNABLE";
  } else {
    return "DOWNLOADABLE";
  }
};

// Tabular files above the cap are still shown, but only a byte-prefix preview
// unless the user opts into a full load with "Load full table".
const isOversizedTabular = (
  reportType: keyof typeof REPORT_COMPONENTS,
  fileSize: number
): boolean => reportType === "TABULAR" && fileSize >= MAXIMUM_TABULAR_FILE_SIZE;

const DEFAULTVIEW = {
  label: "Default View",
  value: "defaultView",
  icon: <PresentationChartBarIcon className="h-6 w-6" />,
};

const APPVIEW = {
  label: "App View",
  value: "appView",
  icon: <CubeIcon className="h-6 w-6" />,
};

const displayOptions = [APPVIEW, DEFAULTVIEW];

function ReportViewerContent({
  runUUID,
  report,
  reportTree,
  activeRunTab,
}: {
  runUUID: string;
  report: RunReportResource | undefined;
  reportTree: RunReportResource[];
  activeRunTab?: RunTabs;
}): JSX.Element {
  const { setSidebarExpanded } = useGlobal();
  const { data: runAppStatus, refetch: refetchRunAppStatus } =
    useRunAppStatus(runUUID);
  const { isPending: isLaunchAppLoading } = useLaunchApplication();
  const {
    mutate: terminateApp,
    isPending: isTerminateAppLoading,
    isSuccess: isTerminateAppSuccess,
    isError: isTerminateAppError,
  } = useTerminateAppInstance();

  const [showDisplayTypeSelection, setShowDisplayTypeSelection] =
    useState<boolean>(false);
  const [selectedDisplayType, setSelectedDisplayType] = useState(APPVIEW);
  const [selectedDir, setSelectedDir] = useState<string>("");
  const [toggleFullScreen, setToggleFullScreen] = useState<boolean>(false);

  // Memoize getContext to prevent infinite re-renders
  const getReportContext = useCallback(
    () => ({
      filePath: report?.routePath || "",
      runUUID,
      reportName: report?.name,
      reportExtension: report?.extension,
      reportFileSize: report?.fileSize,
    }),
    [report, runUUID]
  );

  useRegisterCortexContext({
    routePattern: ROUTES.RUN_DETAILS,
    contextName: `Report Viewer: ${report?.name}`,
    getContext: getReportContext,
    systemPrompt:
      "You are analyzing a report file. Help answer questions about the file content.",
    enabled: activeRunTab === RunTabs.Reports,
  });

  useEffect(() => {
    if (
      report?.reportTypes.includes("apps") &&
      (!report?.appFileExtension ||
        report?.appFileExtension?.toLowerCase() ===
          report?.extension?.toLowerCase())
    ) {
      setShowDisplayTypeSelection(true);
      setSelectedDir(getDirectoryName(reportTree, "", report.id));
      setSelectedDisplayType(APPVIEW);
    } else {
      setShowDisplayTypeSelection(false);
      setSelectedDisplayType(DEFAULTVIEW);
    }
  }, [report]);

  useEffect(() => {
    if (isTerminateAppSuccess || isTerminateAppError) {
      refetchRunAppStatus();
    }
  }, [refetchRunAppStatus, isTerminateAppSuccess, isTerminateAppError]);

  const selectedRunAppStatus = useMemo(() => {
    return runAppStatus?.find(
      ({ filename, dir }) => filename === report?.name && dir === selectedDir
    );
  }, [runAppStatus, report?.name, selectedDir]);

  // Hooks must run unconditionally on every render — these were previously
  // below the `if (!report)` early return, which changed the hook count when
  // `report` transitioned undefined -> defined and crashed the tab.
  const reportType = report
    ? getReportType(report.extension, report.fileSize, report.reportTypes)
    : "DOWNLOADABLE";
  const isPercentTabular =
    reportType === "TABULAR" && !!report?.reportTypes.includes("table-percent");
  const reportUrl = getReportUrl(report?.routePath ?? "");
  // The url the user chose to load in full. Cleared whenever the selected
  // report changes, so returning to an oversized table opens the preview
  // again rather than the full table (#1509). Reset during render, not in an
  // effect, so no commit ever starts the worker for the returning report.
  const [fullLoadUrl, setFullLoadUrl] = useState<string>("");
  const [shownReportUrl, setShownReportUrl] = useState(reportUrl);
  if (shownReportUrl !== reportUrl) {
    setShownReportUrl(reportUrl);
    setFullLoadUrl("");
  }
  const isOversized = isOversizedTabular(reportType, report?.fileSize ?? 0);
  const previewOnly = isOversized && fullLoadUrl !== reportUrl;

  // SINGLE worker for the file, owned here and shared by both consumers:
  // `TabularReport` (rendering, via the `worker` prop below) and
  // `handleDownloadWithPercentages` (the export, below). Calling
  // `useReportTable` a second time — once here and once inside
  // `TabularReport` — would download and parse the same file twice, doubling
  // network/CPU/memory on exactly the `table-percent` reports the export
  // exists for.
  //
  // `enabled` must stay false whenever:
  //  - the report isn't actually tabular (reportType !== "TABULAR") — a PDF
  //    or image has no business being fetched and parsed as CSV/TSV;
  //  - `previewOnly` is true — this is the 25MB cap: an oversized tabular
  //    file gets a bounded byte-prefix preview instead (see
  //    `TabularReport`'s own `previewQuery`), and the worker must never
  //    eagerly download/parse the WHOLE oversized file in the background.
  //    It only does so after the user clicks "Load full table";
  //  - the Reports tab isn't the active tab — leaving the tab (component
  //    stays mounted) must tear the worker down, not let it keep streaming
  //    off-screen.
  //
  // Deliberately NOT gated on `selectedDisplayType`: a report that is both
  // TABULAR by extension and "apps"-tagged (App View available) keeps the
  // worker streaming in the background even while App View is what's shown.
  // This is bounded wasted work, not a correctness bug — `TabularReport`
  // itself is unmounted while App View is active, so there's no stale render
  // for the streamed data to leak into. Adding `selectedDisplayType` here
  // would tear the worker down and restart it on every view toggle, which is
  // a bigger behavioural change than this gate is meant to make.
  const isWorkerEnabled =
    reportType === "TABULAR" &&
    !previewOnly &&
    activeRunTab === RunTabs.Reports;
  const worker = useReportTable(reportUrl, isWorkerEnabled);

  // Single source of truth for "the whole file has streamed in and there's
  // at least one column to export" — used both as the backstop inside
  // `handleDownloadWithPercentages` and to disable the menu item itself, so
  // the two can't drift out of sync with each other.
  const isExportReady = worker.isComplete && worker.columns.length > 0;

  if (!report) {
    return <div></div>;
  }

  const ReportComponent =
    selectedDisplayType.value === APPVIEW.value
      ? REPORT_COMPONENTS.RUNNABLE
      : REPORT_COMPONENTS[reportType];

  const handleDownload = () => {
    const link = document.createElement("a");
    link.setAttribute("download", report?.name);
    link.href = reportUrl;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadWithPercentages = () => {
    // Backstop: the menu option itself is disabled until `isExportReady`
    // (see the ContextButton options below), but guard here too so this can
    // never run against a partial file — exporting the first N streamed rows
    // of a still-in-flight parse as if it were the whole table would be
    // worse than not offering the export at all.
    if (!isExportReady) {
      return;
    }

    const columns = worker.columns;
    // `worker.rows` is `string[][]`, column-ordered positionally (not
    // keyed) — the same shape `TabularReport` converts below. Re-key by
    // column here rather than re-deriving from raw text: this is exactly
    // what avoids re-fetching/re-parsing the whole file a second time.
    const cleanedRows = worker.rows.map(row => {
      const record: Record<string, unknown> = {};
      for (let j = 0; j < row.length; j++) {
        record[columns[j]] = row[j];
      }
      return record;
    });

    const percentRows = processPercentData(cleanedRows);

    const baseName = report.name.replace(/\.(tsv|csv)$/i, "");
    const fileName = `${baseName}_with_percentages.tsv`;
    const content = toTsv({ headers: columns, rows: percentRows });

    downloadTextFile({
      content,
      fileName,
      mimeType: "text/tab-separated-values;charset=utf-8",
    });
  };

  const handleTerminateApp = () => {
    terminateApp(selectedRunAppStatus.id);
  };

  const handleToggleFullScreen = () => {
    setToggleFullScreen(!toggleFullScreen);
    setSidebarExpanded(toggleFullScreen);
  };

  const handleDownloadAction = (option: TableContextMenuInterface) => {
    switch (option.name) {
      case "Download Raw":
        handleDownload();
        break;
      case "Download with Percentages":
        handleDownloadWithPercentages();
        break;
    }
  };

  return (
    <div
      className={classNames(
        toggleFullScreen
          ? "absolute left-0 top-[60px] z-[99] h-full w-full bg-white px-[25px]"
          : "h-full w-full"
      )}>
      <div className="mb-4 flex items-center justify-end gap-2">
        {showDisplayTypeSelection && (
          <div className="flex flex-1 gap-2">
            <span className="flex items-center font-bold">View Mode:</span>
            <span className="isolate inline-flex rounded-md shadow-sm">
              {displayOptions.map((option, index) => (
                <React.Fragment key={option.value}>
                  <button
                    id={`displayType-${option.value}`}
                    key={option.value}
                    className={classNames(
                      "relative inline-flex h-9 items-center  px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                      selectedDisplayType.value === option.value
                        ? "bg-indigo-600 text-white hover:bg-indigo-500  hover:shadow-md"
                        : "bg-white",
                      index === 0 ? "rounded-l-md" : "rounded-r-md"
                    )}
                    onClick={() => setSelectedDisplayType(option)}>
                    {option.icon}
                  </button>
                  <Tooltip
                    anchorSelect={`#displayType-${option.value}`}
                    content={option.label}
                  />
                </React.Fragment>
              ))}
            </span>
          </div>
        )}
        {selectedDisplayType.value === APPVIEW.value &&
          !isLaunchAppLoading &&
          (selectedRunAppStatus?.status === AppStatus.Running ||
            selectedRunAppStatus?.status === AppStatus.Creating) && (
            <>
              <button
                disabled={isTerminateAppLoading}
                className="flex items-center space-x-2"
                onClick={handleTerminateApp}>
                <PowerIcon className="h-5 w-5" id="terminateAppIcon" />
              </button>
              <Tooltip
                anchorSelect="#terminateAppIcon"
                content="Terminate App"
              />
            </>
          )}
        {selectedDisplayType.value === APPVIEW.value &&
          selectedRunAppStatus?.accessUrl &&
          selectedRunAppStatus?.status === AppStatus.Running &&
          !isLaunchAppLoading && (
            <>
              <Link
                to={selectedRunAppStatus?.accessUrl}
                target="_blank"
                rel="noreferrer">
                <GlobeAltIcon className="h-5 w-5" id="openAppIcon" />
              </Link>
              <Tooltip
                anchorSelect="#openAppIcon"
                content="Open App in a New Window"
              />
            </>
          )}
        {selectedDisplayType.value === DEFAULTVIEW.value && (
          <>
            <Link
              to={getReportUrl(report?.routePath)}
              target="_blank"
              rel="noreferrer">
              <ArrowTopRightOnSquareIcon
                className="h-5 w-5"
                id="openReportIcon"
              />
            </Link>
            <Tooltip
              anchorSelect="#openReportIcon"
              content="Open in a New Window"
            />
          </>
        )}
        <button
          onClick={handleToggleFullScreen}
          className="flex items-center space-x-2">
          {toggleFullScreen ? (
            <ArrowsPointingInIcon className="h-5 w-5" id="toggleFullScreen" />
          ) : (
            <ArrowsPointingOutIcon className="h-5 w-5" id="toggleFullScreen" />
          )}
        </button>
        <Tooltip
          anchorSelect="#toggleFullScreen"
          content="Toggle Full Screen"
        />
        {isPercentTabular ? (
          <ContextButton
            text="Download"
            options={[
              { name: "Download Raw", href: "#" },
              {
                name: "Download with Percentages",
                href: "#",
                // Disabled (not silently partial) until the whole file has
                // streamed in. A preview-capped report never reaches
                // `isComplete` while `enabled` is false, so this also covers
                // "no percentages export for a report we only preview".
                disabled: !isExportReady,
              },
            ]}
            onSelect={handleDownloadAction}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 items-center gap-1"
          />
        ) : (
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2">
            <ArrowDownTrayIcon className="h-5 w-5" id="downloadReportIcon" />
          </button>
        )}
      </div>
      <ReportComponent
        runUUID={runUUID}
        report={report}
        reportTree={reportTree}
        previewOnly={previewOnly}
        worker={worker}
        onLoadFullTable={
          isOversized ? () => setFullLoadUrl(reportUrl) : undefined
        }
      />
    </div>
  );
}

export function ReportViewer({
  runUUID,
  report,
  reportTree,
  activeRunTab,
}: {
  runUUID: string;
  report: RunReportResource | undefined;
  reportTree: RunReportResource[];
  activeRunTab?: RunTabs;
}): JSX.Element {
  return (
    <SocketProvider socketUri="/file-summarize">
      <ReportViewerContent
        runUUID={runUUID}
        report={report}
        reportTree={reportTree}
        activeRunTab={activeRunTab}
      />
    </SocketProvider>
  );
}
